import {
  createContext,
  useCallback,
  useContext,
  useState,
  type FunctionComponent,
  type PropsWithChildren,
} from 'react'
import AddKolsToPredictiveReportModal from '@/components/common/modals/add-kols-to-predictive-report-modal'
import SelectCollaborativeItemsForKolModal, {
  type SelectCollaborativeItemsForKolModalProps,
} from '@/components/predictive-report/modals/select-collaborative-items-for-kol-modal'

type OpenAddKolsToPredictiveReportModalProps = Pick<
  SelectCollaborativeItemsForKolModalProps,
  'candidateList' | 'from' | 'isClickedFromDrawer'
>

type AddKolsToPredictiveReportModalContextProps = (
  props: OpenAddKolsToPredictiveReportModalProps,
) => void

const AddKolsToPredictiveReportModalContext = createContext<
  AddKolsToPredictiveReportModalContextProps | undefined
>(undefined)

export const useAddKolsToPredictiveReportModal =
  (): AddKolsToPredictiveReportModalContextProps => {
    const context = useContext(AddKolsToPredictiveReportModalContext)
    if (!context) {
      throw new Error(
        'useAddKolsToPredictiveReportModal must be used within a AddKolsToPredictiveReportModalProvider',
      )
    }
    return context
  }

export const AddKolsToPredictiveReportModalProvider: FunctionComponent<
  PropsWithChildren
> = ({ children }) => {
  const [contextProps, setContextProps] =
    useState<OpenAddKolsToPredictiveReportModalProps>()
  const [
    selectCollaborativeItemsForKolModalProps,
    setSelectCollaborativeItemsForKolModalProps,
  ] = useState<Omit<SelectCollaborativeItemsForKolModalProps, 'onClose'>>()

  const handleCloseModal = useCallback(() => {
    setContextProps(undefined)
    setSelectCollaborativeItemsForKolModalProps(undefined)
  }, [])

  const handleClickAdd = useCallback(
    (reportId: number) => {
      setSelectCollaborativeItemsForKolModalProps({
        ...contextProps,
        id: reportId,
        open: true,
      })
    },
    [contextProps],
  )

  return (
    <AddKolsToPredictiveReportModalContext.Provider value={setContextProps}>
      {children}
      {contextProps && (
        <AddKolsToPredictiveReportModal
          {...contextProps}
          onClickAdd={handleClickAdd}
          onClose={handleCloseModal}
        />
      )}
      {selectCollaborativeItemsForKolModalProps && (
        <SelectCollaborativeItemsForKolModal
          {...selectCollaborativeItemsForKolModalProps}
          onClose={() => {
            setSelectCollaborativeItemsForKolModalProps(undefined)
          }}
        />
      )}
    </AddKolsToPredictiveReportModalContext.Provider>
  )
}
